import React, { useState } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CustomerDetailSkeleton } from "@/components/LoadingSkeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Edit,
  Trash2,
  Mail,
  Phone,
  Calendar,
  User,
  Clock,
  Ticket,
  AlertCircle,
  Save,
  X,
  UserX,
} from "lucide-react";
import { OrganizationBadge } from "../components/OrganizationBadge";
import { CustomerEraseDialog } from "../components/CustomerEraseDialog";
import {
  useCustomer,
  useCustomerTickets,
  useUpdateCustomer,
  useArchiveCustomer,
  useRestoreCustomer,
  useDeleteCustomer,
} from "@/hooks/useCustomers";
import { useAdminOrganizations } from "@/hooks/useAdminOrganizations";
import { useDateFormat } from "@/hooks/useDateFormat";
import { showToast } from "@/lib/toast";
import { getErrorMessage } from "@/lib/apiErrors";
import {
  TicketStatusLabels,
  PriorityLabels,
  TicketStatusColors,
  PriorityColors,
} from "@/types/tickets";
import type { UpdateCustomerRequest } from "@/types/customers";

export const CustomerDetail: React.FC = () => {
  const { id } = useParams({ strict: false });
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { formatDateTime } = useDateFormat();

  // Fetch customer data - only if id is defined
  const { data: customer, isLoading, error, isError } = useCustomer(id || "");
  const ticketsResponse = useCustomerTickets(id || "");
  const tickets = ticketsResponse.data || [];
  const ticketsLoading = ticketsResponse.isLoading;

  // Mutations
  const updateCustomerMutation = useUpdateCustomer();
  const archiveCustomerMutation = useArchiveCustomer();
  const restoreCustomerMutation = useRestoreCustomer();
  const deleteCustomerMutation = useDeleteCustomer();

  // Archive, restore and delete need Admin in the customer's own organization
  const { data: adminOrgs } = useAdminOrganizations();
  const isAdminForCustomer =
    !!customer?.organizationId &&
    (adminOrgs ?? []).some((org) => org.id === customer.organizationId);
  const isArchived = !!customer?.archivedAt;
  // Erased: personal data replaced with placeholders - no editing, restoring or deleting
  const isErased = !!customer?.erasedAt;
  const [eraseDialogOpen, setEraseDialogOpen] = useState(false);

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");

  // Update edit state when customer data changes
  React.useEffect(() => {
    if (customer) {
      setEditFirstName(customer.firstName || "");
      setEditLastName(customer.lastName || "");
      setEditEmail(customer.email || "");
      setEditPhone(customer.phoneNumber || "");
    }
  }, [customer]);

  const handleEditModeToggle = () => {
    if (!editMode && customer) {
      // Entering edit mode - refresh all edit state with current customer values
      setEditFirstName(customer.firstName || "");
      setEditLastName(customer.lastName || "");
      setEditEmail(customer.email || "");
      setEditPhone(customer.phoneNumber || "");
    }
    setEditMode(!editMode);
  };

  const handleUpdate = async () => {
    if (!customer || !id) return;

    const updateRequest: UpdateCustomerRequest = {
      firstName: editFirstName.trim(),
      lastName: editLastName.trim(),
      email: editEmail.trim(),
      phone: editPhone.trim() || undefined,
    };

    try {
      await updateCustomerMutation.mutateAsync({ id, updates: updateRequest });
      showToast.success(
        "Customer Updated",
        "Customer information has been updated successfully."
      );
      setEditMode(false);
    } catch (error) {
      console.error("Failed to update customer:", error);
      showToast.error(
        "Update Failed",
        getErrorMessage(error, "Failed to update customer. Please try again.")
      );
    }
  };

  const handleArchive = async () => {
    if (!id) return;
    try {
      await archiveCustomerMutation.mutateAsync(id);
      showToast.success(
        "Customer Archived",
        "The customer is hidden from customer lists. Their tickets are kept."
      );
    } catch (error) {
      console.error("Failed to archive customer:", error);
      showToast.error(
        "Archive Failed",
        getErrorMessage(error, "Failed to archive customer. Please try again.")
      );
    }
  };

  const handleRestore = async () => {
    if (!id) return;
    try {
      await restoreCustomerMutation.mutateAsync(id);
      showToast.success(
        "Customer Restored",
        "The customer is active again."
      );
    } catch (error) {
      console.error("Failed to restore customer:", error);
      showToast.error(
        "Restore Failed",
        getErrorMessage(error, "Failed to restore customer. Please try again.")
      );
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteCustomerMutation.mutateAsync(id);
      showToast.success(
        "Customer Deleted",
        "The customer has been permanently deleted."
      );
      navigateTo.customers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      showToast.error(
        "Delete Failed",
        getErrorMessage(error, "Failed to delete customer. Please try again.")
      );
    }
  };

  if (!id) {
    return <div>Customer ID is required</div>;
  }

  if (isLoading) {
    return <CustomerDetailSkeleton />;
  }

  if (isError || !customer) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to={getPath.customers()}>
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Customer Not Found
            </h1>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error?.message || "Customer not found or could not be loaded."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Link to={getPath.customers()}>
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-foreground">
                {customer.fullName}
              </h1>
              {customer.organizationId && customer.organizationName && (
                <OrganizationBadge organizationName={customer.organizationName} />
              )}
              {isErased ? (
                <Badge variant="secondary" className="gap-1">
                  <UserX className="h-3 w-3" />
                  Erased
                </Badge>
              ) : isArchived && (
                <Badge variant="secondary" className="gap-1">
                  <Archive className="h-3 w-3" />
                  Archived
                </Badge>
              )}
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              Customer since {formatDateTime(customer.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          {editMode ? (
            <>
              <Button
                onClick={handleUpdate}
                disabled={updateCustomerMutation.isPending}
                className="w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateCustomerMutation.isPending
                  ? "Saving..."
                  : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={handleEditModeToggle} className="w-full sm:w-auto">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : isErased ? null : (
            <>
              <Button onClick={handleEditModeToggle} className="w-full sm:w-auto">
                <Edit className="h-4 w-4 mr-2" />
                Edit Customer
              </Button>
              {isAdminForCustomer && isArchived && (
                <Button
                  variant="outline"
                  onClick={() => setEraseDialogOpen(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-950 w-full sm:w-auto"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Erase Personal Data
                </Button>
              )}
              {isAdminForCustomer && isArchived && (
                <Button
                  variant="outline"
                  onClick={handleRestore}
                  disabled={restoreCustomerMutation.isPending}
                  className="w-full sm:w-auto"
                >
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  {restoreCustomerMutation.isPending ? "Restoring..." : "Restore Customer"}
                </Button>
              )}
              {isAdminForCustomer && !isArchived && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto">
                      <Archive className="h-4 w-4 mr-2" />
                      Archive Customer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Archive {customer.fullName}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        They'll be hidden from customer lists and pickers, removed as a
                        participant from tickets that aren't closed, and signed out of
                        the customer portal, and they can't sign in or register again.
                        Their tickets and history are kept. Email from them is held in
                        Org Settings → Held Emails for an admin to review. You can
                        restore them at any time.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleArchive}
                        disabled={archiveCustomerMutation.isPending}
                      >
                        {archiveCustomerMutation.isPending
                          ? "Archiving..."
                          : "Archive Customer"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {/* Only a customer with no tickets can be deleted; the server also checks CCs, comments and history */}
              {isAdminForCustomer && customer.totalTickets === 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-950 w-full sm:w-auto"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Customer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Permanently delete {customer.fullName}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This can't be undone. Use this for a customer added by
                        mistake. A customer with any ticket history can't be
                        deleted; archive them instead.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={deleteCustomerMutation.isPending}
                      >
                        {deleteCustomerMutation.isPending
                          ? "Deleting..."
                          : "Delete Customer"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </>
          )}
        </div>
      </div>

      {/* Erased / archived banner */}
      {isErased ? (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <UserX className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-medium text-foreground">
              This customer's personal data was erased
              {customer.erasedAt ? ` on ${formatDateTime(customer.erasedAt)}` : ""}.
            </p>
            <p className="text-muted-foreground">
              The tickets they requested were permanently deleted and their messages on other tickets removed. If
              they contact you again, they'll be a new customer.
            </p>
          </div>
        </div>
      ) : isArchived && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <Archive className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-medium text-foreground">
              This customer was archived
              {customer.archivedAt ? ` on ${formatDateTime(customer.archivedAt)}` : ""}.
            </p>
            <p className="text-muted-foreground">
              They're hidden from customer lists and pickers, can't sign in to the
              customer portal, and their tickets are kept. Email from them is held in
              Org Settings → Held Emails until an admin restores them or discards it.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Customer Information */}
        <div className="lg:col-span-2">
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Customer Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  First Name
                </label>
                {editMode ? (
                  <Input
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    placeholder="First name"
                  />
                ) : (
                  <p className="text-foreground font-medium">
                    {customer.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  Last Name
                </label>
                {editMode ? (
                  <Input
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    placeholder="Last name"
                  />
                ) : (
                  <p className="text-foreground font-medium">
                    {customer.lastName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Mail className="inline h-4 w-4 mr-1" />
                  Email Address
                </label>
                {editMode ? (
                  <Input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="Email address"
                  />
                ) : (
                  <p className="text-foreground font-medium">
                    {customer.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Phone Number
                </label>
                {editMode ? (
                  <Input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                ) : (
                  <p className="text-foreground font-medium">
                    {customer.phoneNumber || "Not provided"}
                  </p>
                )}
              </div>

              {/* Last Updated */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Last Updated
                </label>
                <p className="text-foreground font-medium">
                  {formatDateTime(customer.updatedAt)}
                </p>
              </div>
            </div>
          </Card>

          {/* Customer Tickets */}
          <Card className="p-4 sm:p-6 mt-4 sm:mt-6">
            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Recent Tickets</h2>

            {ticketsLoading ? (
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-48 mb-4"></div>
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 bg-muted rounded"></div>
                  ))}
                </div>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8">
                <Ticket className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No tickets found for this customer.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Ticket #
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                        Priority
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                        Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-background divide-y divide-border">
                    {tickets.slice(0, 10).map((ticket: import('../types/tickets').TicketResponse) => (
                      <tr
                        key={ticket.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigateTo.ticket(ticket.id)}
                      >
                        <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap">
                          <span className="text-blue-600 font-medium text-sm">
                            {ticket.ticketNumber}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3">
                          <div className="max-w-[150px] sm:max-w-48">
                            <div
                              className="text-foreground font-medium text-xs sm:text-sm block truncate"
                              title={ticket.subject}
                            >
                              {ticket.subject}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${TicketStatusColors[ticket.status]}`}
                          >
                            {TicketStatusLabels[ticket.status]}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap hidden sm:table-cell">
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${PriorityColors[ticket.priority]}`}
                          >
                            {PriorityLabels[ticket.priority]}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap text-xs text-muted-foreground hidden md:table-cell">
                          {formatDateTime(ticket.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tickets.length > 10 && (
              <div className="mt-4">
                {/* The ticket list filtered to this customer (in their organization) */}
                <Link
                  to={getPath.tickets()}
                  search={{
                    customers: [customer.id],
                    organizationIds: customer.organizationId ? [customer.organizationId] : undefined,
                    page: 1,
                    pageSize: 20,
                    sortBy: 'createdat',
                    sortDescending: true,
                  }}
                >
                  <Button variant="outline" className="w-full">
                    View All {tickets.length} Tickets
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        </div>

        {/* Customer Stats Sidebar */}
        <div className="space-y-4 md:space-y-6">
          {/* Ticket Stats */}
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Ticket Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-foreground">Total</span>
                </div>
                <Badge variant="secondary">{customer.totalTickets}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm text-foreground">Open</span>
                </div>
                <Badge variant="secondary">{customer.openTickets}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-foreground">Resolved</span>
                </div>
                <Badge variant="secondary">{customer.resolvedTickets}</Badge>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {isAdminForCustomer && isArchived && !isErased && (
        <CustomerEraseDialog
          customerId={customer.id}
          customerName={customer.fullName}
          customerEmail={customer.email}
          ticketCount={customer.totalTickets}
          open={eraseDialogOpen}
          onOpenChange={setEraseDialogOpen}
        />
      )}
    </div>
  );
};
